import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'antd';
import dayjs from 'dayjs';
import AppBar from '../components/AppBar';
import { DOC_CATEGORY_TONE, EmptyState, I, SearchBar, Skeletons } from '../components/kit';
import * as documentsApi from '../api/documents';
import { fileFormat } from '../lib/format';

const fmtSize = (b) => {
  const n = Number(b) || 0;
  if (!n) return '';
  return n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`;
};

const ALL = '__ALL__';

// Chip tone -> the row icon's tint, so a document's colour is the same on the
// list, its filter pill and its detail page.
const TONE_COLOR = {
  brand: 'var(--primary)', ok: 'var(--ok)', warn: 'var(--orange)',
  purple: 'var(--purple)', accent: 'var(--accent)', muted: 'var(--muted)',
};
const toneOf = (category) => DOC_CATEGORY_TONE[category] || 'brand';

/**
 * Company documents library — a file repository, not a feed.
 *
 * 14+ documents at ~270px per card meant two per screen and a lot of scrolling
 * to find anything. This is a dense list instead: one tappable row per file with
 * a type icon, name, one line of purpose and its size, plus search and category
 * filters to narrow it down.
 *
 * The rows keep HQ's curated `sort_order` rather than grouping into category
 * sections — that order is deliberately drag-arranged in the admin portal, and
 * folder-style grouping would silently override it. Categories are a filter here,
 * not a hierarchy.
 *
 * "Printed by you" (?view=printed) is the second half of the screen: the numbered
 * documents this agent has printed, each saved with the values it was printed
 * with, newest change first. Opening one reopens the workspace on that copy, and
 * printing it there keeps its Ref No. — so a vendor changing their mind is an edit,
 * not a new letter. In the URL so Back from the workspace lands on this list.
 */
export default function DownloadsPage() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'printed' ? 'printed' : 'forms';
  const setView = (v) => setParams(v === 'printed' ? { view: 'printed' } : {}, { replace: true });
  const [docs, setDocs] = useState(null);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState(ALL);

  useEffect(() => {
    let on = true;
    documentsApi.listDocuments()
      .then((r) => { if (on) setDocs(r.data || []); })
      .catch(() => { if (on) setDocs([]); });
    return () => { on = false; };
  }, []);

  // Counts come from the search-filtered set, so a pill never promises rows the
  // current search cannot show.
  const searched = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return docs || [];
    return (docs || []).filter((d) => [d.title, d.description, d.category]
      .some((v) => String(v || '').toLowerCase().includes(term)));
  }, [docs, q]);

  const categories = useMemo(() => {
    const seen = [];
    for (const d of docs || []) if (d.category && !seen.includes(d.category)) seen.push(d.category);
    return seen;
  }, [docs]);

  const rows = useMemo(
    () => (category === ALL ? searched : searched.filter((d) => d.category === category)),
    [searched, category],
  );

  // Same pill as the Booking forms page's status filters — this screen is that
  // screen's sibling (a pushed list with a search + a filter row), so it uses
  // the same chrome rather than inventing a second style of filter pill.
  const pill = (key, label) => {
    const active = category === key;
    return (
      <div
        key={key}
        onClick={() => setCategory(key)}
        style={{
          flex: 'none',
          fontSize: 12.5,
          fontWeight: 600,
          padding: '6px 13px',
          borderRadius: 999,
          cursor: 'pointer',
          background: active ? 'var(--primary)' : 'var(--surface2)',
          color: active ? '#fff' : 'var(--muted)',
          border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
        }}
      >
        {label}
      </div>
    );
  };

  const tab = (key, text) => {
    const active = view === key;
    return (
      <div
        key={key}
        onClick={() => setView(key)}
        style={{
          flex: 1, textAlign: 'center', fontSize: 12.5, fontWeight: 600, padding: '7px 0', borderRadius: 9, cursor: 'pointer',
          background: active ? 'var(--surface)' : 'transparent',
          color: active ? 'var(--text)' : 'var(--muted)',
          boxShadow: active ? '0 1px 2px rgba(0,0,0,.08)' : 'none',
        }}
      >
        {text}
      </div>
    );
  };
  const tabs = (
    <div style={{ display: 'flex', gap: 4, padding: 3, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 11 }}>
      {tab('forms', 'All forms')}
      {tab('printed', 'Printed by you')}
    </div>
  );

  if (view === 'printed') {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <AppBar back title="CID Forms" sub="standard letters & forms" />
        <PrintedList tabs={tabs} onOpen={(r) => nav(`/downloads/${r.document_id}?issue=${r.id}`)} />
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="CID Forms" sub="standard letters & forms" />

      {/* Search + category filters stay pinned; only the file list scrolls. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        {tabs}
        <SearchBar onSearch={setQ} placeholder="Search name or purpose…" />
        {categories.length > 1 && (
          <div className="scrx" style={{ display: 'flex', gap: 8, overflowX: 'auto' }}>
            {pill(ALL, 'All')}
            {categories.map((c) => pill(c, c.charAt(0) + c.slice(1).toLowerCase()))}
          </div>
        )}
      </div>

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '15px 16px' }}>
        {docs === null ? (
          <Skeletons n={7} height={64} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={docs.length ? 'No matching documents' : 'No documents yet'}
            sub={docs.length ? 'Try a different search or category.' : "HQ hasn't published any downloadable documents."}
          />
        ) : (
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
            {rows.map((d, i) => (
              <div
                key={d.id}
                onClick={() => nav(`/downloads/${d.id}`)}
                style={{
                  display: 'flex',
                  gap: 14,
                  alignItems: 'center',
                  padding: '14px 15px',
                  cursor: 'pointer',
                  borderTop: i ? '1px solid var(--border)' : 'none',
                }}
              >
                <div
                  style={{
                    width: 38, height: 38, borderRadius: 10, flex: 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'color-mix(in srgb, var(--border) 45%, transparent)',
                    color: TONE_COLOR[toneOf(d.category)] || 'var(--primary)',
                  }}
                >
                  <I.doc width={19} height={19} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35 }}>{d.title}</div>
                  {d.description && (
                    <div
                      style={{
                        fontSize: 11.5, color: 'var(--muted)', marginTop: 2, lineHeight: 1.4,
                        display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                      }}
                    >
                      {d.description}
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, fontSize: 11.5, color: 'var(--muted)' }}>
                    <span>{fileFormat(d)}{d.file_size_bytes ? ` · ${fmtSize(d.file_size_bytes)}` : ''}</span>
                    {d.prefill_available && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--primary)', fontWeight: 600 }}>
                        <I.pen width={11} height={11} />
                        Fillable
                      </span>
                    )}
                  </div>
                </div>

                <I.chevR width={16} height={16} style={{ color: 'var(--muted)', flex: 'none' }} />
              </div>
            ))}
          </div>
        )}
        <div style={{ height: 20 }} />
      </div>
    </div>
  );
}

const PAGE = 20;

/**
 * The agent's printed copies — searched and paged on the server, unlike the
 * library above, because this list only grows. A failed load says so rather than
 * showing "nothing printed yet" (empty-is-never-a-safe-fetch-default).
 */
function PrintedList({ tabs, onOpen }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let on = true;
    setRows(null);
    setError(null);
    documentsApi.listIssuedDocuments({ page: 1, limit: PAGE, search: q || undefined })
      .then((r) => {
        if (!on) return;
        setRows(r.data || []);
        setPage(1);
        setHasMore((r.pagination?.totalPages || 1) > 1);
      })
      .catch((e) => { if (on) { setRows([]); setError(e?.message || 'Could not load your printed documents'); } });
    return () => { on = false; };
  }, [q]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const r = await documentsApi.listIssuedDocuments({ page: page + 1, limit: PAGE, search: q || undefined });
      setRows((cur) => [...(cur || []), ...(r.data || [])]);
      setPage(page + 1);
      setHasMore((r.pagination?.totalPages || 1) > page + 1);
    } catch (e) {
      setError(e?.message || 'Could not load more');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', flex: 'none' }}>
        {tabs}
        <SearchBar onSearch={setQ} placeholder="Search Ref No., name or address…" />
      </div>
      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '15px 16px' }}>
        {rows === null ? (
          <Skeletons n={5} height={64} />
        ) : error && !rows.length ? (
          <EmptyState title="Could not load your printed documents" sub={error} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={q ? 'Nothing matches' : 'Nothing printed yet'}
            sub={q ? 'Try a different Ref No. or address.' : 'Letters you print from CID Forms are kept here, so you can open them again and change them.'}
          />
        ) : (
          <>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginBottom: 10, lineHeight: 1.5 }}>
              Open one to change it — printing it again keeps its Ref No.
            </div>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
              {rows.map((r, i) => (
                <div
                  key={r.id}
                  onClick={() => onOpen(r)}
                  style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '13px 15px', cursor: 'pointer', borderTop: i ? '1px solid var(--border)' : 'none' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.35 }}>{r.title}</div>
                    {r.subject && (
                      <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.subject}</div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 4, fontSize: 11.5, color: 'var(--muted)' }}>
                      <span style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{r.doc_ref}</span>
                      <span>· printed {dayjs(r.last_printed_at).format('D MMM YYYY')}</span>
                      {r.print_count > 1 && <span>· {r.print_count}×</span>}
                    </div>
                  </div>
                  <I.chevR width={16} height={16} style={{ color: 'var(--muted)', flex: 'none' }} />
                </div>
              ))}
            </div>
            {hasMore && (
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <Button size="small" loading={loadingMore} onClick={loadMore}>Load more</Button>
              </div>
            )}
          </>
        )}
        <div style={{ height: 20 }} />
      </div>
    </>
  );
}
